import { useNavigate } from 'react-router-dom'
import { useApp } from '../store/AppContext'
import { Avatar, roleLabels } from '../components/Avatar'
import { AnimatedCompanion } from '../components/AnimatedCompanion'
import { BrandLogo } from '../components/BrandLogo'
import { ensureYouthDemoContent, setYouthDemoProfile, youthDemoProfiles } from '../lib/youthDemo'
import type { YouthParticipantId } from '../lib/youthParticipants'

const TAGLINE = 'Accessible communication. Connected care. For every stage of life.'

export function SwitchUser({ initial = false }: { initial?: boolean }) {
  const { allUsers, currentUser, setCurrentUserId, setAccountType, tasks, addTask } = useApp()
  const navigate = useNavigate()

  const choose = (id: string) => {
    setYouthDemoProfile(false)
    if (id === 'u-alex') setAccountType('adult')
    setCurrentUserId(id)
    if (id === 'u-mia' || id === 'u-jordan' || id === 'u-olivia') {
      ensureYouthDemoContent(tasks, addTask)
      navigate('/home')
      return
    }
    if (id === 'u-lillian' || id === 'u-casey') {
      ensureYouthDemoContent(tasks, addTask)
      navigate('/youth-workspace')
      return
    }
    navigate('/today')
  }

  const chooseYouth = (participantId: YouthParticipantId) => {
    setYouthDemoProfile(true, participantId)
    setAccountType('youth')
    ensureYouthDemoContent(tasks, addTask)
    setCurrentUserId(participantId)
    navigate('/today')
  }

  const byId = (id: string) => allUsers.find((u) => u.id === id)
  const participant = byId('u-alex')
  const nominee = byId('u-mark')
  const coordinator = byId('u-tom')
  const supportWorkers = allUsers.filter((u) => u.role === 'support_worker' && u.id !== 'u-casey')
  const alliedHealth = allUsers.filter((u) => u.role === 'allied_health')
  const childFamilyAndWorker = allUsers.filter((u) => ['u-lillian', 'u-casey'].includes(u.id))

  const accountCard = (u: typeof allUsers[number], accent: string) => (
    <button
      key={u.id}
      onClick={() => choose(u.id)}
      className={`flex w-full items-center gap-3 rounded-[20px] border bg-white p-3.5 text-left shadow-[0_5px_18px_rgba(15,23,42,.045)] transition active:scale-[.99] ${currentUser.id === u.id && !initial ? 'border-[#8bb6da] ring-2 ring-[#e3edf7]' : 'border-slate-200'}`}
    >
      <Avatar user={u} size={44} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-black text-slate-900">{u.name}</p>
        <p className="truncate text-[11px] font-semibold text-slate-500">{u.title ?? roleLabels[u.role]}</p>
      </div>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-sm font-black ${accent}`}>→</span>
    </button>
  )

  return (
    <div className="min-h-screen bg-[#f5f8fb] text-slate-900">
      <div className="mx-auto w-full max-w-md px-4 pb-10 pt-[calc(env(safe-area-inset-top)+22px)]">
        <header className="overflow-hidden rounded-[30px] border border-[#dce5ee] bg-white shadow-[0_14px_36px_rgba(36,79,132,.08)]">
          <div className="relative px-5 pb-5 pt-5">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#245da8] via-[#7657b5] to-[#ee68b1]" />
            <BrandLogo variant="lockup" className="mx-auto h-auto w-full max-w-[390px]" priority />
            <p className="mx-auto mt-1 max-w-[300px] text-center text-[13px] font-bold leading-5 text-slate-600">{TAGLINE}</p>
          </div>
          <div className="border-t border-[#e7edf3] bg-[#fbfcfe] px-5 py-4">
            <h1 className="text-[18px] font-black">{initial ? 'Choose your view' : 'Switch account'}</h1>
            <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">Open the participant, family or professional workspace linked to your support circle.</p>
          </div>
        </header>

        <div className="mt-4 space-y-4">
          <section>
            <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[.12em] text-[#245a9d]">Participant & family</p>
            <div className="space-y-2">
              {participant && accountCard(participant, 'bg-[#edf4ff] text-[#245a9d]')}
              {nominee && accountCard(nominee, 'bg-[#e8f7f5] text-[#218b89]')}
            </div>
          </section>

          <section className="rounded-[24px] border border-[#efd7e6] bg-gradient-to-r from-[#fff7fb] via-white to-[#faf7ff] p-3.5 shadow-[0_6px_20px_rgba(15,23,42,.04)]">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[.12em] text-[#c84a91]">Child / youth</p>
                <p className="mt-1 text-xs font-bold text-slate-600">Assistive technology view with Nixi™</p>
              </div>
              <span className="rounded-full bg-[#fff0f7] px-2.5 py-1 text-[10px] font-black text-[#c84a91]">Nixi™</span>
            </div>
            <div className="grid gap-2">
              {youthDemoProfiles.map((profile) => <button
                key={profile.id}
                onClick={() => chooseYouth(profile.id)}
                className="flex w-full items-center gap-3 rounded-[20px] bg-white p-3.5 text-left shadow-[0_4px_14px_rgba(15,23,42,.04)] transition active:scale-[.99]"
              >
                <img src={profile.photoUrl} alt={`${profile.name} profile`} className="h-14 w-14 shrink-0 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="text-base font-black">{profile.name}</p>
                  <p className="mt-0.5 text-[11px] font-semibold text-slate-500">Child participant · Age {profile.age}</p>
                  <p className="mt-1 text-[10px] font-black text-[#7558b5]">Lillian Carter · Mum / Plan Nominee</p>
                </div>
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-2xl bg-[#f0ecfb]">
                  <div className="origin-top-left scale-[.35]"><AnimatedCompanion colour="pink" size="small" happy /></div>
                </div>
              </button>)}
            </div>
            {childFamilyAndWorker.length > 0 && <div className="mt-3 grid grid-cols-1 gap-2">{childFamilyAndWorker.map((u) => accountCard(u, 'bg-[#f0ecfb] text-[#7558b5]'))}</div>}
          </section>

          <section>
            <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[.12em] text-[#245a9d]">Support workers</p>
            <div className="space-y-2">{supportWorkers.map((u) => accountCard(u, 'bg-[#fff2e7] text-[#df7d31]'))}</div>
          </section>

          <section>
            <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[.12em] text-[#245a9d]">Allied health</p>
            <div className="space-y-2">{alliedHealth.map((u) => accountCard(u, 'bg-[#f0ecfb] text-[#7558b5]'))}</div>
          </section>

          {coordinator && <section>
            <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[.12em] text-[#245a9d]">Support coordination</p>
            {accountCard(coordinator, 'bg-[#e8f7f5] text-[#218b89]')}
          </section>}
        </div>
      </div>
    </div>
  )
}

